import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import Tesseract from "tesseract.js";
import { ExecuteLayoutEventMethods } from "../../../General/commonFunctions";

export const FactsOCRTextReading = (props) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [extractedValue, setExtractedValue] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [linePosition, setLinePosition] = useState(0); // Line position for scanning effect
  const [lineDirection, setLineDirection] = useState(1); // 1 for down, -1 for up

  const isMobileDevice = () => {
    return /Mobi|Android/i.test(navigator.userAgent);
  };

  const videoConstraints = {
    facingMode: isMobileDevice() ? "environment" : "user",
  };

  const detectText = async (imgSrc) => {
    try {
      setIsProcessing(true); // Set processing flag
      const {
        data: { text: extractedText, words },
      } = await Tesseract.recognize(imgSrc, "eng", {
        rectangles: true, // Enable bounding boxes
      });
      setExtractedValue(extractedText); // Set the raw extracted text
      drawSparkles(words);

      // Call ExecuteLayoutEventMethods after successful scan
      ExecuteLayoutEventMethods(props.whenscan, extractedText);
    } catch (error) {
      console.error("Error recognizing text:", error);
    } finally {
      setIsProcessing(false); // Reset processing flag after operation
    }
  };

  const drawSparkles = (words) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const webcam = webcamRef.current;

    if (!canvas || !context || !webcam || !webcam.video) {
      console.error("Canvas, context, or webcam not available");
      return;
    }

    const videoWidth = webcam.video.videoWidth;
    const videoHeight = webcam.video.videoHeight;

    // Set canvas dimensions only once per render
    if (canvas.width !== videoWidth || canvas.height !== videoHeight) {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
    }

    const scaleX = canvas.width / videoWidth;
    const scaleY = canvas.height / videoHeight;

    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Draw sparkle effect
    const sparkleCount = 20; // Number of sparkles per word
    words.forEach(({ bbox, confidence }) => {
      if (confidence > 85) { // Filter by confidence level
        const x0 = bbox.x0 * scaleX;
        const y0 = bbox.y0 * scaleY;
        const x1 = bbox.x1 * scaleX;
        const y1 = bbox.y1 * scaleY;

        for (let i = 0; i < sparkleCount; i++) {
          const randomX = x0 + Math.random() * (x1 - x0);
          const randomY = y0 + Math.random() * (y1 - y0);
          const size = Math.random() * 2 + 1;
          context.beginPath();
          context.arc(randomX, randomY, size, 0, 2 * Math.PI, false);
          context.fillStyle = `rgba(0, 255, 0, 0.8)`; 
          context.fill();
        }
      }
    });
  };

  const drawScanningLine = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!canvas || !context) return;

    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Draw scanning line
    context.fillStyle = "rgba(0, 255, 0, 0.6)";
    context.fillRect(0, linePosition, canvas.width, 5); // Green line
  };

  useEffect(() => {
    const lineInterval = setInterval(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const nextPosition = linePosition + lineDirection * 5; // Move the line
      if (nextPosition <= 0 || nextPosition >= canvas.height) {
        setLineDirection(lineDirection * -1); // Reverse direction
      } else {
        setLinePosition(nextPosition);
      }

      drawScanningLine(); // Redraw the line
    }, 30);

    return () => clearInterval(lineInterval); 
  }, [linePosition, lineDirection]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (webcamRef.current && !isProcessing) {
        const imgSrc = webcamRef.current.getScreenshot();
        if (imgSrc) {
          detectText(imgSrc);
        }
      }
    }, 1000); // Adjust interval to 1 second for real-time processing

    return () => clearInterval(interval); 
  }, [isProcessing]);

  return (
    <div
      style={{
        ...props.styles?.container,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}
    >
      <div
        style={{
          ...props.styles?.videoContainer,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "400px",
          position: "relative",
        }}
      >
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            zIndex: 1,
          }}
        />
        {isProcessing && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "10px 20px",
              borderRadius: "10px",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            Scanning...
          </div>
        )}
      </div>
    </div>
  );
};
